import React, { useCallback, useEffect, useState } from 'react';
import { get, orderBy } from 'lodash';
import {
  ATMField,
  ATMGrid,
  ATMHeader,
  ATMInput,
  ATMSelect,
  formatPhoneNumber,
} from 'shared-it-appmod-ui';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';
import { useFlightOperatorContext } from 'src/contexts/flight-operator.context';
import Lang from 'src/libraries/language';
import { IFlightPilot } from 'src/models/flight-operator.model';
import { IRequestFormMethods } from 'src/models/request.model';
import { getFlightOperatorStatus } from 'src/selectors/flight-operator.selector';
import { actionTypes } from 'src/ducks/flight-operator.duck';

type IProps = IRequestFormMethods & {
  category: string;
};

const FlightRequestContactInformationForm: React.FC<IProps> = ({
  category,
  control,
  errors,
}) => {
  const { state, actions } = useFlightOperatorContext();

  const [pilotInCommand, setPilotInCommand] = useState<IFlightPilot[]>([]);

  useEffect(() => {
    if (actions) actions.listGET(true);
  }, [actions]);

  useEffect(() => {
    const value = get(
      control,
      `defaultValuesRef.current.${category}.operatorName`
    );

    if (value && state.list.length) {
      const list = state.list.find((item) => value === item.name);
      setPilotInCommand(list?.Pilots ?? []);
    }
  }, [state.list, category, control, setPilotInCommand]);

  const handleChange = useCallback(
    (value) => {
      const operator = state.list.find((item) => value === item.name);
      setPilotInCommand(orderBy(operator?.Pilots ?? [], ['name'], ['asc']));

      control.setValue(`${category}.operatorId`, operator?.operatorId);
      control.setValue(`${category}.pilotName`, undefined);
      control.setValue(`${category}.pilotPhone`, undefined);
    },
    [state, control, category, setPilotInCommand]
  );

  const handlePhone = useCallback(
    (value) => {
      const pilot = pilotInCommand.find((item) => item.name === value);

      if (pilot) {
        control.setValue(
          `${category}.pilotPhone`,
          formatPhoneNumber(pilot.phone ?? '')
        );
      } else {
        control.setValue(`${category}.pilotPhone`, undefined);
      }
    },
    [control, category, pilotInCommand]
  );

  const status = getFlightOperatorStatus(
    state,
    actionTypes.FLIGHT_OPERATOR_FETCH
  );

  return (
    <>
      <ATMHeader size="small" className="text-underline">
        {Lang.TTL_CONTACT_INFORMATION}
      </ATMHeader>

      <ATMGrid columns={3}>
        <ATMGrid.Row>
          <ATMGrid.Column>
            <ATMField
              name={`${category}.operatorId`}
              as={ATMInput}
              control={control}
              type="hidden"
              className="hidden"
            />

            <ATMField
              name={`${category}.operatorName`}
              label={Lang.LBL_FLIGHT_OPERATOR}
              placeholder={Lang.LBL_SELECT}
              as={ATMSelect}
              control={control}
              options={state.list.map((item) => ({
                key: item.operatorId,
                value: item.name,
                text: item.name,
              }))}
              onChange={([_, { value }]) => {
                handleChange(value);

                return value;
              }}
              selectOnBlur={false}
              error={get(errors, `${category}.operatorName`, false)}
              loading={!state.list.length && status.fetching}
              disabled={!state.list.length && status.fetching}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              key={`pilotName_${state.list.length}`}
              name={`${category}.pilotName`}
              label={Lang.LBL_PILOT_IN_COMMAND}
              placeholder={Lang.LBL_SELECT}
              as={ATMSelect}
              options={pilotInCommand.map((item) => ({
                key: item.pilotId,
                value: item.name,
                text: item.name,
              }))}
              control={control}
              onChange={([_, { value }]) => {
                handlePhone(value);
                return value;
              }}
              selectOnBlur={false}
              error={get(errors, `${category}.pilotName`, false)}
              loading={!state.list.length && status.fetching}
              disabled={!state.list.length && status.fetching}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name={`${category}.pilotPhone`}
              label={Lang.LBL_PILOT_NUMBER}
              placeholder={Lang.LBL_ENTER}
              as={ATMInput}
              control={control}
              error={get(errors, `${category}.pilotPhone`, false)}
              isPhone
              onChange={([_, { value }]) => value}
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
        <ATMGrid.Row>
          <ATMGrid.Column>
            <ATMField
              name={`${category}.primeContractor`}
              label={<LabelRequired>{Lang.LBL_PRIME_CONTRACTOR}</LabelRequired>}
              placeholder={Lang.LBL_ENTER}
              as={ATMInput}
              control={control}
              error={get(errors, `${category}.primeContractor`, false)}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name={`${category}.primeName`}
              label={<LabelRequired>{Lang.LBL_CONTACT_NAME}</LabelRequired>}
              placeholder={Lang.LBL_ENTER}
              as={ATMInput}
              control={control}
              error={get(errors, `${category}.primeName`, false)}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name={`${category}.primePhone`}
              label={
                <LabelRequired>{Lang.LBL_CONTRACTOR_NUMBER}</LabelRequired>
              }
              placeholder={Lang.LBL_ENTER}
              as={ATMInput}
              control={control}
              error={get(errors, `${category}.primePhone`, false)}
              isPhone
              onChange={([_, { value }]) => value}
              autoComplete="off"
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
        <ATMGrid.Row>
          <ATMGrid.Column>
            <ATMField
              name={`${category}.primeEmail`}
              label={<LabelRequired>{Lang.LBL_CONTRACTOR_EMAIL}</LabelRequired>}
              placeholder={Lang.LBL_ENTER}
              as={ATMInput}
              control={control}
              error={get(errors, `${category}.primeEmail`, false)}
              onChange={([_, { value }]) => value}
              autoComplete="off"
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
      </ATMGrid>
    </>
  );
};

export default FlightRequestContactInformationForm;
